/**
 * Disable2FAModal — potwierdzenie wyłączenia 2FA (F7b T7b.5).
 *
 * Backend (`POST /api/auth/2fa/disable`, F5 T5.11) wymaga tylko `currentPassword`
 * (decyzja D11=A, plan-compliant). Jeśli security paranoja duża, backend może
 * w przyszłości wymagać też TOTP — frontend dodałby wtedy drugie pole.
 *
 * Error map:
 *  - 401 INVALID_PASSWORD → inline alert pod polem.
 *  - 422 VALIDATION_FAILED → generic alert.
 *  - inne → generic alert.
 *
 * Po sukcesie: `onSuccess()` callback (MyProfile wywoła `refreshMe()` + toast).
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

export interface Disable2FAModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function Disable2FAModal({ open, onClose, onSuccess }: Disable2FAModalProps) {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setCurrentPassword('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/auth/2fa/disable', { currentPassword });
      onSuccess();
    } catch (err) {
      const axErr = err as AxiosError<{ error?: string }>;
      const status = axErr.response?.status ?? null;
      const code = axErr.response?.data?.error ?? null;
      if (status === 401 && code === 'INVALID_PASSWORD') {
        setError(t('tenant.2fa.disable.errors.invalidPassword'));
      } else if (status === 422) {
        setError(t('tenant.2fa.disable.errors.validation'));
      } else {
        setError(t('tenant.2fa.disable.errors.generic'));
      }
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => (submitting ? undefined : onClose())}
      title={t('tenant.2fa.disable.title')}
      description={t('tenant.2fa.disable.description')}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('tenant.2fa.disable.cancel')}
          </Button>
          <Button
            type="submit"
            form="disable-2fa-form"
            variant="danger"
            loading={submitting}
            disabled={submitting || !currentPassword}
          >
            {submitting ? t('tenant.2fa.disable.submitting') : t('tenant.2fa.disable.confirm')}
          </Button>
        </>
      }
    >
      <form id="disable-2fa-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <p className="text-sm text-text-tertiary">{t('tenant.2fa.disable.body')}</p>
        <Input
          label={t('tenant.2fa.disable.passwordLabel')}
          type="password"
          autoComplete="current-password"
          required
          autoFocus
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          disabled={submitting}
        />
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
